import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import {
  CircularProgress,
  InputAdornment,
  TextField,
  type TextFieldProps,
  Tooltip,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useUrlValidation } from "../hooks/use-url-validation";
import { LogoDev } from "./logo-dev";

/**
 * Props for ValidatedUrlInput component
 */
export interface ValidatedUrlInputProps extends Omit<TextFieldProps, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  showValidation?: boolean;
  debounceMs?: number;
}

/**
 * Helper function to normalize URL with protocol
 */
function normalizeUrl(url: string): string {
  if (!url.trim()) return url;

  // If URL doesn't start with a protocol, add https://
  if (!url.match(/^https?:\/\//i)) {
    return `https://${url}`;
  }

  return url;
}

/**
 * ValidatedUrlInput - URL input component with real-time validation
 *
 * Wraps Material UI TextField with integrated URL validation,
 * showing visual feedback (checkmark/cross icons) and tooltips
 * with validation status and latency.
 *
 * @example
 * <ValidatedUrlInput
 *   label="Application URL"
 *   value={url}
 *   onChange={setUrl}
 *   onValidationChange={setIsValid}
 * />
 */
export function ValidatedUrlInput({
  value,
  onChange,
  onValidationChange,
  showValidation = true,
  debounceMs = 600,
  ...textFieldProps
}: ValidatedUrlInputProps) {
  // Use validation hook
  const { status, tooltipText, isValid } = useUrlValidation(value, {
    debounceMs,
    enabled: showValidation && !!value,
  });

  // Generate icon based on validation status
  const icon = useMemo(() => {
    switch (status) {
      case "loading":
        return <CircularProgress size={14} />;
      case "valid":
        return <CheckCircleIcon sx={{ color: "success.main", fontSize: 14 }} />;
      case "warning":
        return <WarningIcon sx={{ color: "warning.main", fontSize: 14 }} />;
      case "invalid":
        return <CancelIcon sx={{ color: "error.main", fontSize: 14 }} />;
      case "error":
        return <ErrorIcon sx={{ color: "error.main", fontSize: 14 }} />;
      default:
        return null;
    }
  }, [status]);

  // Notify parent when validation state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // Handle blur to normalize URL
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim();
    if (inputValue && !inputValue.match(/^https?:\/\//i)) {
      const normalizedValue = normalizeUrl(inputValue);
      onChange(normalizedValue);
    }

    // Call the original onBlur handler if provided
    if (textFieldProps.onBlur) {
      textFieldProps.onBlur(e);
    }
  };

  // Build input adornments
  const startAdornment = useMemo(() => {
    if (!icon) return undefined;

    return (
      <InputAdornment position="start">
        <Tooltip title={tooltipText} arrow placement="top">
          <span style={{ display: "flex", alignItems: "center" }}>{icon}</span>
        </Tooltip>
      </InputAdornment>
    );
  }, [icon, tooltipText]);

  const endAdornment = useMemo(() => {
    if (!value) return undefined;

    return (
      <InputAdornment position="end">
        <LogoDev url={value} />
      </InputAdornment>
    );
  }, [value]);

  return (
    <TextField
      {...textFieldProps}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={handleBlur}
      type="url"
      slotProps={{
        ...textFieldProps.slotProps,
        input: {
          ...textFieldProps.slotProps?.input,
          startAdornment,
          endAdornment,
        },
      }}
    />
  );
}
